import { Injectable, signal, WritableSignal } from '@angular/core';
import { Router } from '@angular/router';
import { JsUser } from '../_interfaces/user.interface';
import {
  Auth,
  GoogleAuthProvider,
  signInWithPopup,
  onAuthStateChanged,
  User,
} from '@angular/fire/auth';
import { serverTimestamp } from '@angular/fire/firestore';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { FirebaseService } from './firebase.service';
import { IndexDbService } from './index-db.service';
import { StorageService } from './storage.service';
import { BehaviorSubject, Observable } from 'rxjs';
import { InviteService } from './invite.service';
import { FirestoreStoreService } from './firestore-store.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  // Observable for auth state - used by the guard
  private authStateSubject = new BehaviorSubject<User | null>(null);
  public authState$: Observable<User | null> =
    this.authStateSubject.asObservable();

  // Track if auth state has been initialized (first check complete)
  public authInitialized: WritableSignal<boolean> = signal(false);

  // Signal to track login error message
  public loginError: WritableSignal<string | null> = signal(null);

  constructor(
    private router: Router,
    private ss: StorageService,
    private fbs: FirebaseService,
    private auth: Auth,
    private idb: IndexDbService,
    private inviteService: InviteService,
    private afs: AngularFirestore,
    private store: FirestoreStoreService
  ) {
    // Listen to Firebase Auth state changes
    this.initAuthStateListener();
  }

  /**
   * Initialize Firebase Auth state listener
   * This ensures we always have the correct auth state
   */
  private initAuthStateListener(): void {
    onAuthStateChanged(this.auth, (user) => {
      this.authStateSubject.next(user);
      this.authInitialized.set(true);

      if (user) {
        // User is signed in - sync with localStorage
        this.ss.setUser(user);
      } else {
        // User is signed out - clear all local data and redirect to login
        const wasLoggedIn = this.ss.getUser()?.uid;
        this.ss.clearLocalStore();

        // Only redirect if user was previously logged in (session expired)
        // and we're not already on the login page
        if (wasLoggedIn && !this.router.url.includes('/login')) {
          console.warn('Session expired - redirecting to login');

          // Clear IndexedDB
          this.idb.fullReset().catch(err =>
            console.error('Failed to clear IndexedDB on session expiration:', err)
          );

          // Clear Firestore store signals and listeners
          this.store.destroy();

          // Redirect with expired flag
          this.router.navigate(['/login'], {
            queryParams: { expired: 'true' },
            replaceUrl: true
          });
        }
      }
    });
  }

  async loginWithGoogle() {
    const provider = new GoogleAuthProvider();
    this.loginError.set(null); // Clear any previous error

    try {
      const result = await signInWithPopup(this.auth, provider);
      const user = result.user;
      const email = user?.email || '';
      const domain = email.split('@')[1] || '';

      // Step 1: Check domain restriction
      if (domain !== 'realtime-innovations.com') {
        this.loginError.set('Access denied. Only @realtime-innovations.com accounts are allowed.');
        await this.logoutUserSilent();
        return;
      }

      // Step 2: Check if user is invited (admin is always allowed)
      let isFirstLogin = false;
      if (email !== this.ADMIN_EMAIL) {
        const invitee = await this.inviteService.checkInviteStatus(email);

        if (!invitee) {
          this.loginError.set('Access denied. You have not been invited to use this application. Please contact an administrator.');
          await this.logoutUserSilent();
          return;
        }

        // Update invite status - track if this is first login
        if (invitee.status === 'pending') {
          isFirstLogin = true;
          await this.inviteService.markInviteAccepted(invitee.id, user.uid);
        } else {
          await this.inviteService.updateLastLogin(invitee.id);
        }
      } else {
        // Admin user - check if user record exists
        const adminExists = await this.checkUserExists(user.uid);
        isFirstLogin = !adminExists;
      }

      // Step 3: Proceed with login
      this.ss.setUser(user);

      // Create user record if first login (based on invite status, not Firebase metadata)
      if (isFirstLogin) {
        await this.createUser(user);
      } else {
        // Update lastLoginAt for existing users
        await this.updateUserLastLogin(user.uid);
      }

      // Navigate to the default route (emails)
      this.router.navigate(['/emails']);
    } catch (error) {
      console.error('Login error:', error);
      this.loginError.set('An error occurred during login. Please try again.');
    }
  }

  /**
   * Silent logout - doesn't navigate, used when rejecting unauthorized users
   * Also clears all local data including IndexedDB and Firestore store
   */
  private async logoutUserSilent() {
    this.ss.clearLocalStore();
    await this.idb.fullReset();
    this.store.destroy();
    await this.auth.signOut();
  }

  /**
   * Check if user record exists in Firestore
   */
  private async checkUserExists(uid: string): Promise<boolean> {
    try {
      const doc = await this.afs.collection('users').doc(uid).get().toPromise();
      return doc?.exists ?? false;
    } catch (error) {
      console.error('Failed to check user existence:', error);
      return false;
    }
  }

  /**
   * Update user's last login timestamp
   */
  private async updateUserLastLogin(uid: string): Promise<void> {
    try {
      await this.afs.collection('users').doc(uid).update({
        lastLoginAt: serverTimestamp(),
      });
    } catch (error) {
      console.error('Failed to update last login:', error);
    }
  }

  /**
   * Full logout - clears all local data (localStorage, sessionStorage, IndexedDB, Firestore store)
   * and navigates to login page
   */
  async logoutUser() {
    // Clear localStorage and sessionStorage
    this.ss.clearLocalStore();

    // Full reset of IndexedDB and service state
    await this.idb.fullReset();

    // Clear Firestore store signals and listeners
    this.store.destroy();

    // Sign out from Firebase Auth
    await this.auth.signOut();

    // Navigate to login
    this.router.navigate(['/login']);
  }

  async createUser(user: any) {
    await this.fbs.createItems<JsUser>(
      [
        {
          createdAt: new Date(),
          createdBy: user?.uid || '',
          deletedAt: null,
          email: user?.email || '',
          id: user?.uid || '',
          name: user.displayName || '',
          uid: user?.uid || '',
          picture: user?.photoURL || '',
          domain: user?.email?.split('@')[1] || '',
          verifiedEmail: user?.emailVerified || false,
          updatedAt: new Date(),
          roles: [],
          cloudUpdatedAt: serverTimestamp(),
        },
      ],
      'users'
    );
  }

  /**
   * Check if user is logged in.
   * Uses both localStorage (for quick sync check) and Firebase Auth state.
   */
  get isLoggedIn(): boolean {
    // Check localStorage first (sync check for guard)
    const localUser = this.ss.getUser();
    if (localUser && localUser?.uid) {
      return true;
    }
    // Also check Firebase Auth state
    const currentUser = this.auth.currentUser;
    return !!currentUser;
  }

  /**
   * Get the current Firebase Auth user
   */
  get currentUser(): User | null {
    return this.auth.currentUser;
  }

  /**
   * Admin email - only this user can delete assets and published templates
   */
  private readonly ADMIN_EMAIL = 'joseph.manadan@realtime-innovations.com';

  /**
   * Check if the current user is an admin
   * Only admin users can delete assets and published templates
   */
  get isAdmin(): boolean {
    const user = this.ss.getUser();
    return user?.email === this.ADMIN_EMAIL;
  }
}
